// Projeler Listesi — plan §4.3 + design ref #screen-projects-list.
// FilteredTable (search + framework/yıl/müşteri/durum/atanan filtre + pagination) + metric kartları.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  formatReportingPeriod,
  type Project,
  type ProjectsListResponse,
  type ProjectStatus,
  type PublishedFrameworksResponse,
} from "@mahir/shared";
import { api } from "../api/client";
import { useAuthStore } from "../stores/auth-store";
import { FilteredTable, useDebouncedValue, type Column } from "../components/common/FilteredTable";
import { RowLink } from "../components/common/RowLink";
import { buildFrameworkOptions } from "../lib/framework-options";
import { buildAssigneeOptions, type AssigneeOptionSource } from "../lib/assignee-options";
import type { ClientPickerItem } from "../lib/client-picker";
import { ImportedPill } from "../components/report-import/ImportedPill";
import { PERIOD_COPY, periodFilterOptions, yearFilterOptions } from "../lib/period-ui";
import { IMPORT_COPY } from "../lib/report-import-ui";

const PAGE_SIZE = 25;

const STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: "Taslak",
  in_progress: "Sürüyor",
  completed: "Tamamlandı",
};

const STATUS_COLORS: Record<ProjectStatus, { bg: string; fg: string }> = {
  draft: { bg: "var(--color-bg)", fg: "var(--color-text-3)" },
  in_progress: {
    bg: "color-mix(in srgb, var(--color-primary) 14%, white)",
    fg: "var(--color-primary)",
  },
  completed: {
    bg: "color-mix(in srgb, var(--color-success) 10%, white)",
    fg: "var(--color-success)",
  },
};

export function ProjectsList(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const canCreate = user?.permissions["projects.create"] === true;
  const canAssignOther = user?.permissions["projects.view_all"] === true;

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 350);
  const [frameworkVersionId, setFrameworkVersionId] = useState("");
  const [reportingYear, setReportingYear] = useState("");
  // Raporlama dönemi filtresi (2C) — boş = tümü.
  const [periodType, setPeriodType] = useState("");
  // Müşteri facet'i ClientPicker (sunucu aramalı) — ad çipte görünsün diye id+ad tutulur.
  const [client, setClient] = useState<ClientPickerItem | null>(null);
  const clientId = client?.id ?? "";
  const [status, setStatus] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  // Geçmiş Rapor Aktarımı 4A/R13 — "" (tümü) | "import" | "manual".
  const [source, setSource] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ProjectsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Filtre options için lookup'lar — ilk yüklemede al
  const [frameworks, setFrameworks] = useState<PublishedFrameworksResponse["frameworks"]>([]);
  const frameworkOptions = useMemo(() => buildFrameworkOptions(frameworks), [frameworks]);

  useEffect(() => {
    void api<PublishedFrameworksResponse>("/admin/frameworks/published")
      .catch(() => ({ frameworks: [] as PublishedFrameworksResponse["frameworks"] }))
      .then((fw) => setFrameworks(fw.frameworks));
  }, []);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    frameworkVersionId,
    reportingYear,
    periodType,
    clientId,
    status,
    assignedTo,
    source,
  ]);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (frameworkVersionId) params.set("frameworkVersionId", frameworkVersionId);
    if (reportingYear) params.set("reportingYear", reportingYear);
    if (periodType) params.set("periodType", periodType);
    if (clientId) params.set("clientId", clientId);
    if (status) params.set("status", status);
    if (assignedTo) params.set("assignedTo", assignedTo);
    if (source) params.set("source", source);
    params.set("page", String(page));
    params.set("limit", String(PAGE_SIZE));

    void api<ProjectsListResponse>(`/projects?${params.toString()}`)
      .then((res) => {
        if (!aborted) {
          setData(res);
          setError(null);
        }
      })
      .catch((err) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Liste yüklenemedi");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [
    debouncedSearch,
    frameworkVersionId,
    reportingYear,
    periodType,
    clientId,
    status,
    assignedTo,
    source,
    page,
    refreshTick,
  ]);

  // Sunucudan tekil raporlama yılları (sayfadan bağımsız — eski yıllı proje başka sayfadaysa da
  // süzgeçte görünür; arşiv `/archive/filters` ile aynı kalıp). Hata halinde takvim penceresi kalır.
  const [serverYears, setServerYears] = useState<number[]>([]);
  // Finar Ö1 — danışman süzgeci: projesi olan iç kullanıcılar, pasifler "(pasif)" (sunucu süzer; müşteri
  // kullanıcıları ve kullanıcı listesinin 50 tavanı yok).
  const [assignees, setAssignees] = useState<AssigneeOptionSource[]>([]);
  useEffect(() => {
    let aborted = false;
    void api<{ years: number[]; assignees?: AssigneeOptionSource[] }>("/projects/filters")
      .then((res) => {
        if (aborted) return;
        setServerYears(res.years);
        setAssignees(res.assignees ?? []);
      })
      .catch(() => {
        /* takvim penceresi yeter */
      });
    return () => {
      aborted = true;
    };
  }, [refreshTick]);

  // İ4 — yalnız veride olan yıllar (takvim penceresi kaldırıldı).
  const yearOptions = useMemo(
    () =>
      yearFilterOptions(
        serverYears,
        (data?.projects ?? []).map((p) => p.reportingYear),
      ),
    [data, serverYears],
  );

  // G12 — `anyFilter` ve `clearFilters` AYNI alan kümesini tanır (`assignedTo`
  // asimetrisi de kapandı); `source` 4A ile eklendi.
  const anyFilter = Boolean(
    debouncedSearch ||
    frameworkVersionId ||
    reportingYear ||
    periodType ||
    clientId ||
    status ||
    assignedTo ||
    source,
  );
  const clearFilters = (): void => {
    setSearch("");
    setFrameworkVersionId("");
    setReportingYear("");
    setPeriodType("");
    setClient(null);
    setStatus("");
    setAssignedTo("");
    setSource("");
  };

  const columns: Column<Project>[] = [
    {
      key: "client",
      label: "Müşteri",
      render: (p) => <RowLink to={`/projects/${p.id}`}>{p.clientName}</RowLink>,
    },
    {
      key: "framework",
      label: "Çerçeve",
      render: (p) => (
        <span>
          {p.frameworkName}{" "}
          <span
            style={{
              color: "var(--color-text-3)",
              fontFamily: "var(--font-mono)",
              fontSize: "0.8rem",
            }}
          >
            {p.versionNumber}
          </span>
        </span>
      ),
    },
    {
      key: "year",
      label: PERIOD_COPY.columnLabel,
      align: "right",
      render: (p) => (
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.85rem" }}>
          {formatReportingPeriod(p.reportingYear, p.periodType)}
        </span>
      ),
    },
    {
      key: "status",
      label: "Durum",
      render: (p) => {
        const c = STATUS_COLORS[p.status];
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span
              style={{
                padding: "2px 10px",
                borderRadius: 999,
                background: c.bg,
                color: c.fg,
                fontSize: "0.72rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {STATUS_LABELS[p.status]}
            </span>
            {/* 4A — nötr "aktarıldı" pill'i; yeni kolon AÇILMAZ (e2e seçicileri korunur). */}
            {p.hasImport ? <ImportedPill /> : null}
          </span>
        );
      },
    },
    {
      key: "assigned",
      label: "Danışman",
      render: (p) => (
        <span style={{ color: "var(--color-text-2)", fontSize: "0.85rem" }}>
          {p.assignedToName}
        </span>
      ),
    },
  ];

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)" }}>
      <div className="mb-6 max-w-7xl">
        <div
          style={{
            fontSize: "0.72rem",
            color: "var(--color-primary)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          PROJELER
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1
              className="text-3xl"
              style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
            >
              Rapor Projeleri
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--color-text-2)" }}>
              {data ? `${data.total} proje` : "Yükleniyor…"}
            </p>
          </div>
          {canCreate ? (
            <button
              onClick={() => void navigate("/projects/new")}
              className="px-4 py-2 rounded-lg font-semibold text-sm"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              + Yeni Proje
            </button>
          ) : null}
        </div>
      </div>

      {data ? (
        <div className="grid grid-cols-4 gap-3 mb-6 max-w-7xl">
          <Metric label="Toplam" value={data.metrics.total} />
          <Metric label="Taslak" value={data.metrics.draft} tone="muted" />
          <Metric label="Sürüyor" value={data.metrics.inProgress} tone="primary" />
          <Metric label="Tamamlandı" value={data.metrics.completed} tone="success" />
        </div>
      ) : null}

      {error ? (
        <div
          className="mb-4 px-3 py-2 rounded-lg text-sm max-w-7xl"
          style={{
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            color: "var(--color-error)",
          }}
        >
          {error}
        </div>
      ) : null}

      <div className="max-w-7xl">
        <FilteredTable
          items={data?.projects ?? []}
          columns={columns}
          rowKey={(p) => p.id}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Müşteri veya çerçeve ara…"
          filters={[
            {
              key: "framework",
              label: "Çerçeve (tümü)",
              value: frameworkVersionId,
              onChange: setFrameworkVersionId,
              options: frameworkOptions,
            },
            {
              key: "year",
              label: "Yıl (tümü)",
              value: reportingYear,
              onChange: setReportingYear,
              options: yearOptions,
            },
            {
              key: "period",
              label: PERIOD_COPY.filterLabel,
              value: periodType,
              onChange: setPeriodType,
              options: periodFilterOptions(),
            },
            {
              key: "client",
              kind: "client",
              label: "Müşteri (tümü)",
              value: client,
              onChange: setClient,
            },
            {
              key: "status",
              label: "Durum (tümü)",
              value: status,
              onChange: setStatus,
              options: (["draft", "in_progress", "completed"] as ProjectStatus[]).map((s) => ({
                value: s,
                label: STATUS_LABELS[s],
              })),
            },
            ...(canAssignOther
              ? [
                  {
                    key: "assigned",
                    label: "Danışman (tümü)",
                    value: assignedTo,
                    onChange: setAssignedTo,
                    options: buildAssigneeOptions(assignees),
                  },
                ]
              : []),
            // 4A — Dönem filtresinin sağında; filtre barı `flex-wrap` olduğu için
            // dar ekranda ikinci satıra sarar (G11 kabul).
            {
              key: "source",
              label: IMPORT_COPY.sourceFilterLabel,
              value: source,
              onChange: setSource,
              options: [
                { value: "import", label: IMPORT_COPY.sourceFilterImport },
                { value: "manual", label: IMPORT_COPY.sourceFilterManual },
              ],
            },
          ]}
          total={data?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          loading={loading}
          emptyMessage={
            anyFilter
              ? periodType
                ? PERIOD_COPY.listEmptyPeriod
                : "Filtrelerinize uygun proje bulunamadı."
              : "Henüz proje yok"
          }
          emptyHint={
            anyFilter
              ? PERIOD_COPY.listEmptyPeriodHint
              : "Üstteki 'Yeni proje' butonuyla ilk projeyi oluşturabilirsiniz."
          }
          {...(anyFilter
            ? {
                emptyAction: (
                  <button
                    onClick={clearFilters}
                    className="text-xs"
                    style={{ color: "var(--color-primary)" }}
                  >
                    {PERIOD_COPY.clearFilters}
                  </button>
                ),
              }
            : {})}
          errorMessage={error}
          onRetry={() => setRefreshTick((t) => t + 1)}
          onRowClick={(p) => void navigate(`/projects/${p.id}`)}
        />
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "muted" | "primary" | "success";
}): JSX.Element {
  const color =
    tone === "primary"
      ? "var(--color-primary)"
      : tone === "success"
        ? "var(--color-success)"
        : tone === "muted"
          ? "var(--color-text-3)"
          : "var(--color-text)";
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          color: "var(--color-text-3)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.8rem",
          fontWeight: 600,
          fontFamily: "var(--font-display)",
          color,
          marginTop: 4,
        }}
      >
        {value}
      </div>
    </div>
  );
}
